/**
 * DEVLOGS-TEMP — fetch and XHR capture.
 *
 * THIS SITS ON THE MEDIA-PREFETCH HOT PATH. artifacts/web/src/player/lib/
 * offline-store.ts runs three-concurrent fetches over every item in the sync
 * window — hundreds of files on a cold box. Consequences, all deliberate:
 *
 *  - The ignore check is the FIRST statement, and a match returns the original
 *    call with no record allocated and no closure created.
 *  - The response is NEVER cloned. Cloning a media response buffers the whole
 *    body; on a TV box pulling a 10 MB file that is a real OOM, not a theory.
 *  - The exact Response instance is returned and the exact error object is
 *    rethrown, by identity. Anything else changes caller semantics.
 *
 * `x-request-id` is read off the RESPONSE — the API already stamps it
 * (artifacts/api-server/src/app.ts genReqId) and already CORS-exposes it
 * (exposedHeaders: ["X-Request-Id"]). That makes the client<->server join key
 * free: no outbound header, so no new CORS preflight.
 */

import { LEVELS } from "../level.ts";
import { sanitizeUrl } from "../redact.ts";
import { DEVLOG_SENTINEL_HEADER } from "../transport.ts";
import type { EmitFn, Level } from "../types.ts";

export interface NetCaptureOptions {
  emit: EmitFn;
  now: () => number;
  newSpan: () => string;
  /** Exact ingest URLs. Guard #2 against the transport loop. */
  ignoreUrls: readonly string[];
  /** Whole hosts to skip (Sentry ingest, etc.). */
  ignoreHosts?: readonly string[];
  /** Guard #3: true while our own transport is sending. */
  isSending?: () => boolean;
}

/**
 * True once the page has started tearing down. Every in-flight fetch is about
 * to be killed by the browser; logging each one as a level-50 error filled the
 * errors view with "TypeError: Failed to fetch" on every tab close — pure
 * noise that buried real failures. Wired lazily and defensively: no DOM, no
 * listener, no problem.
 */
let pageUnloading = false;
function watchPageUnload(): void {
  try {
    const target = (globalThis as { addEventListener?: unknown }).addEventListener;
    if (typeof target !== "function") return;
    (globalThis as unknown as Window).addEventListener("pagehide", () => {
      pageUnloading = true;
    });
    // bfcache: a back-button restore resumes the SAME page with the module
    // heap intact — without this reset the latch stays true forever and every
    // later real network failure is downgraded to info, a brand-new way for
    // the pipeline to lie. (Caught in adversarial review before merge.)
    (globalThis as unknown as Window).addEventListener("pageshow", () => {
      pageUnloading = false;
    });
  } catch {
    /* capture must never throw */
  }
}
watchPageUnload();

/**
 * A rejected fetch is only an ERROR when it plausibly reflects a real network
 * failure. Explicit aborts (AbortError) and anything after teardown began are
 * informational: the request was cancelled, nothing is broken.
 */
function fetchRejectionLevel(error: unknown): Level {
  try {
    const name = (error as { name?: unknown } | null)?.name;
    if (name === "AbortError") return LEVELS.info;
    if (pageUnloading) return LEVELS.info;
  } catch {
    /* fall through */
  }
  return LEVELS.error;
}

const PATCH_TAG = Symbol.for("omnimedia.devlog.fetch");

/**
 * The wrapper FORWARDS ITS ARGUMENTS UNTOUCHED and never re-types them.
 *
 * Both an earlier `RequestInfo | URL` (DOM) and a `Parameters<typeof fetch>[0]`
 * (derive-from-ambient) attempt failed to compile on tv/mobile: with the DOM and
 * React Native `fetch` declarations both in scope the type has several call
 * signatures, and `Parameters<...>` picks one while the call target uses
 * another. Since this wrapper inspects the request only through the read-only
 * helpers below and passes the original arguments straight through, `unknown`
 * is both the honest type and the portable one.
 */
type FetchInput = unknown;
type FetchInit = { method?: unknown; headers?: unknown; body?: unknown } | undefined;

/** Forward-only view of the original fetch, free of lib-specific overloads. */
type RawFetch = (...args: unknown[]) => Promise<Response>;

type PatchedFetch = typeof fetch & { [PATCH_TAG]?: { original: typeof fetch } };

export function installFetchCapture(
  scope: { fetch: typeof fetch },
  options: NetCaptureOptions,
): () => void {
  const current = scope.fetch as PatchedFetch | undefined;
  if (typeof current !== "function" || current[PATCH_TAG]) return () => {};

  const original = current;
  const ignore = new Set(options.ignoreUrls);
  const ignoreHosts = new Set(options.ignoreHosts ?? []);

  const forward = original as unknown as RawFetch;

  const patched = function (this: unknown, ...args: unknown[]): Promise<Response> {
    const input = args[0] as FetchInput;
    const init = args[1] as FetchInit;
    // --- guard block: first statements, cheapest possible ------------------
    const url = resolveUrl(input);
    if (url !== null && (ignore.has(url) || hostIgnored(url, ignoreHosts))) {
      return forward.apply(this, args);
    }
    if (options.isSending?.() === true) {
      return forward.apply(this, args);
    }
    if (hasSentinel(init)) {
      return forward.apply(this, args);
    }
    // -----------------------------------------------------------------------

    const method = resolveMethod(input, init);
    const span = safeCall(options.newSpan, "");
    const t0 = safeCall(options.now, 0);

    safeVoid(() =>
      options.emit(
        LEVELS.debug,
        "net.fetch",
        "start",
        {
          method,
          url: sanitizeUrl(url),
          headerKeys: headerNames(init),
          hasBody: init?.body !== undefined && init?.body !== null,
        },
        { span },
      ),
    );

    let result: Promise<Response>;
    try {
      result = forward.apply(this, args);
    } catch (error) {
      safeVoid(() =>
        options.emit(LEVELS.error, "net.fetch", "error", { method, url: sanitizeUrl(url) }, {
          span,
          err: error,
          dur: safeCall(options.now, 0) - t0,
        }),
      );
      throw error;
    }

    return result.then(
      (res) => {
        safeVoid(() => {
          const status = res.status;
          const level: Level =
            status >= 500 ? LEVELS.error : status >= 400 || !res.ok ? LEVELS.warn : LEVELS.debug;
          const reqId = header(res, "x-request-id");
          options.emit(
            level,
            "net.fetch",
            "end",
            {
              method,
              url: sanitizeUrl(url),
              status,
              ok: res.ok,
              type: res.type,
              redirected: res.redirected,
              bytes: numeric(header(res, "content-length")),
              cache: header(res, "cf-cache-status"),
            },
            {
              span,
              dur: safeCall(options.now, 0) - t0,
              ...(reqId ? { reqId } : {}),
            },
          );
        });
        // The identical Response instance. Never a clone, never a wrapper.
        return res;
      },
      (error: unknown) => {
        safeVoid(() =>
          options.emit(fetchRejectionLevel(error), "net.fetch", "error", { method, url: sanitizeUrl(url) }, {
            span,
            err: error,
            dur: safeCall(options.now, 0) - t0,
          }),
        );
        // The identical error object, by identity.
        throw error;
      },
    );
  } as PatchedFetch;

  patched[PATCH_TAG] = { original };
  scope.fetch = patched;

  return () => {
    if (scope.fetch === patched) scope.fetch = original;
  };
}

// --- XHR --------------------------------------------------------------------

interface XhrLike {
  prototype: {
    open: (method: string, url: string, ...rest: unknown[]) => void;
    send: (body?: unknown) => void;
    addEventListener?: (type: string, listener: () => void) => void;
    status?: number;
  };
}

const XHR_STATE = Symbol.for("omnimedia.devlog.xhr");
const XHR_PATCH = Symbol.for("omnimedia.devlog.xhrPatched");

/**
 * WEB/DESKTOP ONLY — deliberately NOT installed on native. React Native's
 * `fetch` is a polyfill over XMLHttpRequest, so wrapping both there yields two
 * records for every request.
 */
export function installXhrCapture(
  scope: { XMLHttpRequest?: XhrLike },
  options: NetCaptureOptions,
): () => void {
  const Xhr = scope.XMLHttpRequest;
  if (!Xhr || typeof Xhr.prototype?.open !== "function") return () => {};
  const proto = Xhr.prototype as unknown as Record<PropertyKey, unknown>;
  if (proto[XHR_PATCH]) return () => {};

  const ignore = new Set(options.ignoreUrls);
  const originalOpen = Xhr.prototype.open;
  const originalSend = Xhr.prototype.send;

  Xhr.prototype.open = function (
    this: Record<PropertyKey, unknown>,
    method: string,
    url: string,
    ...rest: unknown[]
  ): void {
    this[XHR_STATE] = { method, url, t0: 0, span: "" };
    return originalOpen.call(this as never, method, url, ...rest);
  };

  Xhr.prototype.send = function (this: Record<PropertyKey, unknown>, body?: unknown): void {
    const state = this[XHR_STATE] as
      | { method: string; url: string; t0: number; span: string }
      | undefined;
    if (state && !ignore.has(state.url) && options.isSending?.() !== true) {
      state.t0 = safeCall(options.now, 0);
      state.span = safeCall(options.newSpan, "");
      safeVoid(() =>
        options.emit(
          LEVELS.debug,
          "net.xhr",
          "start",
          { method: state.method, url: sanitizeUrl(state.url) },
          { span: state.span },
        ),
      );
      safeVoid(() => {
        const self = this as unknown as {
          addEventListener?: (type: string, listener: () => void) => void;
          status?: number;
        };
        self.addEventListener?.("loadend", () => {
          safeVoid(() => {
            const status = self.status ?? 0;
            options.emit(
              status >= 500 ? LEVELS.error : status >= 400 ? LEVELS.warn : LEVELS.debug,
              "net.xhr",
              "end",
              { method: state.method, url: sanitizeUrl(state.url), status },
              { span: state.span, dur: safeCall(options.now, 0) - state.t0 },
            );
          });
        });
      });
    }
    return originalSend.call(this as never, body);
  };

  proto[XHR_PATCH] = true;

  return () => {
    Xhr.prototype.open = originalOpen;
    Xhr.prototype.send = originalSend;
    delete proto[XHR_PATCH];
  };
}

// --- helpers ----------------------------------------------------------------

function resolveUrl(input: unknown): string | null {
  try {
    if (typeof input === "string") return input;
    if (typeof URL !== "undefined" && input instanceof URL) return input.toString();
    // A Request — read `.url` only. Never touch the body.
    const request = input as { url?: unknown };
    return typeof request.url === "string" ? request.url : null;
  } catch {
    return null;
  }
}

function resolveMethod(input: unknown, init?: FetchInit): string {
  try {
    if (typeof init?.method === "string") return init.method.toUpperCase();
    const request = input as { method?: unknown };
    if (typeof request?.method === "string") return request.method.toUpperCase();
  } catch {
    /* fall through */
  }
  return "GET";
}

function hostIgnored(url: string, hosts: ReadonlySet<string>): boolean {
  if (hosts.size === 0) return false;
  try {
    return hosts.has(new URL(url).host);
  } catch {
    return false;
  }
}

function hasSentinel(init?: FetchInit): boolean {
  try {
    const headers = init?.headers;
    if (!headers) return false;
    if (typeof (headers as Headers).get === "function") {
      return (headers as Headers).get(DEVLOG_SENTINEL_HEADER) !== null;
    }
    if (Array.isArray(headers)) {
      return headers.some(([key]) => String(key).toLowerCase() === DEVLOG_SENTINEL_HEADER);
    }
    return Object.keys(headers as Record<string, string>).some(
      (key) => key.toLowerCase() === DEVLOG_SENTINEL_HEADER,
    );
  } catch {
    return false;
  }
}

/**
 * Header NAMES only. That an `authorization` header was present is diagnostic;
 * its value never leaves the device.
 */
function headerNames(init?: FetchInit): string[] | undefined {
  try {
    const headers = init?.headers;
    if (!headers) return undefined;
    let names: string[];
    if (typeof (headers as Headers).forEach === "function" && !Array.isArray(headers)) {
      names = [];
      (headers as Headers).forEach((_value, key) => names.push(key));
    } else if (Array.isArray(headers)) {
      names = headers.map(([key]) => String(key));
    } else {
      names = Object.keys(headers as Record<string, string>);
    }
    return names.length > 0 ? names.map((n) => n.toLowerCase()).sort() : undefined;
  } catch {
    return undefined;
  }
}

function header(res: Response, name: string): string | undefined {
  try {
    return res.headers?.get(name) ?? undefined;
  } catch {
    return undefined;
  }
}

function numeric(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function safeVoid(fn: () => void): void {
  try {
    fn();
  } catch {
    /* fire-and-forget */
  }
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
